import type React from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import {
  fieldsForStyle,
  normWrapStyle,
  sideEnabledForStyle,
  sideForKey,
} from '../helpers/wrap-style-sides'
import type { OffsetKey, WrapCarrier, WrapTarget } from '../types/wrap-offset'

type ShapeManagerRefObject = React.RefObject<ShapeManagerRef | null> | undefined

/** Writes `value` to every field the shared control owns, for every selected target, straight to
 *  the live manager (canvas-visible immediately) WITHOUT the undo-history/persistence side
 *  effects `commitToTargets` also performs — deliberately skips `onBatchShapesCommitted` and
 *  `setActiveObjects` so a continuous slider drag or fast typing doesn't spam an undo entry (or
 *  an API call) per tick, and so `target.obj` (sourced from the `useActiveObject` context) stays
 *  pinned to the pre-interaction value — which is exactly what the eventual real commit
 *  (`commitAllOffsets`) needs to diff against for a single correct before/after undo entry once
 *  the interaction ends. Each target's own CURRENT style decides which fields it owns
 *  (`fieldsForStyle`) — a Wrap Shape target only gets `wrapOffsetTop` patched even while a
 *  Bounding Box target in the same selection gets all four. With exactly one target this is the
 *  same single `updateShape` call the old single-target `applyLiveOffsetPatch` made. */
export function applyLiveOffsetsToAll(
  shapeManagerRef: ShapeManagerRefObject,
  targets: WrapTarget[],
  value: number
): void {
  if (targets.length === 0) return

  for (const t of targets) {
    const patch: Partial<WrapCarrier> = {}

    for (const { field } of fieldsForStyle(normWrapStyle(t.obj))) patch[field] = value

    if (Object.keys(patch).length === 0) continue

    shapeManagerRef?.current?.updateShape(t.id, patch)
  }
}

/** Same as `applyLiveOffsetsToAll`, but for one specific directional side — only patches targets
 *  for which that side is real for their OWN current style (`sideEnabledForStyle`), so an
 *  independent per-side edit never touches a target's disabled/inapplicable side (AC 4/7). */
export function applyLiveSideOffsetToAll(
  shapeManagerRef: ShapeManagerRefObject,
  targets: WrapTarget[],
  key: OffsetKey,
  value: number
): void {
  if (targets.length === 0) return
  const { field } = sideForKey(key)

  for (const t of targets) {
    if (!sideEnabledForStyle(key, normWrapStyle(t.obj))) continue

    shapeManagerRef?.current?.updateShape(t.id, { [field]: value })
  }
}
